/**
 * What a case is actually investigating.
 *
 * **A case could not say.** `GET /cases/{id}` loaded the subjects only to count them, so
 * the identifiers themselves reached no client, and the create form recorded every subject
 * as a `full_name` whatever it was. An analyst could not see what was being researched,
 * could not correct a typo, and could not add the email address the first run turned up —
 * `USER_GUIDE.md` §2 told them to use `curl`.
 *
 * The normalised form is shown beside the raw one because a surprising normalisation is
 * the usual reason a source returns nothing, and it is otherwise invisible.
 */

import { useState, type FormEvent, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { request } from '@/lib/api';
import type { AddSubjectRequest, CaseSubject } from '@/lib/schemas';
import { Badge, Button, Card, ErrorState, Loading, Table } from '@/components/ui';
import { useCan } from '@/hooks/usePermissions';
import { SubjectImport } from '@/features/cases/SubjectImport';

/**
 * The identifier types the domain accepts, with what each one reaches.
 *
 * Mirrors the `SubjectInputModel` pattern in the API. The "reaches" column is the single
 * most useful thing this screen can tell somebody: a `company` subject is accepted by the
 * API and consumed by no shipped source, so a case with only one collects nothing and
 * looks broken.
 */
const TYPES = [
  { value: 'full_name', label: 'Full name', reaches: 'arxiv · crossref · github · openalex · orcid' },
  { value: 'email', label: 'Email', reaches: 'github · gitlab · gravatar · openalex · orcid' },
  { value: 'username', label: 'Username', reaches: 'github · gitlab' },
  { value: 'domain', label: 'Domain', reaches: 'ctlogs · dns · webpage · whois' },
  { value: 'url', label: 'URL', reaches: 'webpage' },
  { value: 'orcid', label: 'ORCID', reaches: 'crossref · openalex · orcid' },
  { value: 'phone', label: 'Phone', reaches: 'no shipped source' },
  { value: 'company', label: 'Company', reaches: 'no shipped source' },
];

/** The subjects tab for one case. */
export function SubjectsTab({ caseId }: { caseId: string }): ReactElement {
  const canEdit = useCan('create_run_cases');
  const queryClient = useQueryClient();
  const [type, setType] = useState(TYPES[0]!.value);
  const [value, setValue] = useState('');

  const subjects = useQuery({
    queryKey: ['case-subjects', caseId],
    queryFn: () => request<CaseSubject[]>(`/cases/${caseId}/subjects`),
  });

  const add = useMutation({
    mutationFn: () =>
      request<CaseSubject, AddSubjectRequest>(`/cases/${caseId}/subjects`, {
        method: 'POST',
        body: { identifier_type: type, identifier_value: value },
      }),
    onSuccess: () => {
      setValue('');
      void queryClient.invalidateQueries({ queryKey: ['case-subjects', caseId] });
    },
  });

  const remove = useMutation({
    mutationFn: (subjectId: string) =>
      request<null>(`/cases/${caseId}/subjects/${subjectId}`, { method: 'DELETE' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['case-subjects', caseId] }),
  });

  if (subjects.isLoading) return <Loading />;
  if (subjects.error) return <ErrorState error={subjects.error} />;

  const rows = subjects.data ?? [];
  const reaches = TYPES.find((entry) => entry.value === type)?.reaches;

  function onSubmit(event: FormEvent): void {
    event.preventDefault();
    if (value.trim()) add.mutate();
  }

  return (
    <div className="space-y-4">
      <Table
        headers={['Type', 'Value', 'Normalised', 'Primary', ...(canEdit ? [''] : [])]}
        empty="This case has no subjects, so a run would dispatch nothing."
        rows={rows.map((subject) => [
          <Badge key="t">{subject.identifier_type}</Badge>,
          <span key="v" data-testid="subject-value">
            {subject.identifier_value}
          </span>,
          <span key="n" className="font-mono text-xs text-muted">
            {subject.normalized_value}
          </span>,
          subject.is_primary ? <Badge key="p" tone="accent">primary</Badge> : <span key="p">—</span>,
          ...(canEdit
            ? [
                <Button
                  key="x"
                  data-testid={`remove-subject-${subject.identifier_value}`}
                  // The last subject cannot go: a case with none would run, dispatch
                  // nothing and look like a broken platform rather than an empty case.
                  disabled={rows.length <= 1 || remove.isPending}
                  onClick={() => remove.mutate(subject.id)}
                >
                  Remove
                </Button>,
              ]
            : []),
        ])}
      />

      {remove.error != null && <ErrorState error={remove.error} />}

      {canEdit && <SubjectImport caseId={caseId} />}

      {canEdit && (
        <Card title="Add a subject">
          <form onSubmit={onSubmit} className="space-y-3 p-4">
            <p className="text-xs text-muted">
              Investigations do not arrive fully specified. An address or username found in
              the first run is the obvious next subject; adding it here means the next run
              dispatches for it too.
            </p>

            <div className="flex flex-wrap items-end gap-3">
              <label className="block text-sm">
                <span className="text-muted">Type</span>
                <select
                  data-testid="subject-type"
                  value={type}
                  onChange={(event) => setType(event.target.value)}
                  className="mt-1 block rounded border border-line bg-surface px-2 py-1.5 text-sm"
                >
                  {TYPES.map((entry) => (
                    <option key={entry.value} value={entry.value}>
                      {entry.label}
                    </option>
                  ))}
                </select>
              </label>

              <label className="block flex-1 text-sm">
                <span className="text-muted">Identifier</span>
                <input
                  data-testid="subject-input"
                  required
                  value={value}
                  onChange={(event) => setValue(event.target.value)}
                  className="mt-1 w-full rounded border border-line bg-surface px-2 py-1.5 text-sm"
                />
              </label>

              <Button type="submit" tone="primary" disabled={add.isPending}>
                {add.isPending ? 'Adding…' : 'Add'}
              </Button>
            </div>

            <p className="text-xs text-muted">
              Reaches: <span className="font-mono">{reaches}</span>
              {reaches === 'no shipped source' && (
                <strong> — a run will collect nothing for this type.</strong>
              )}
            </p>

            {add.error != null && <ErrorState error={add.error} />}
          </form>
        </Card>
      )}
    </div>
  );
}
